import React, { useState, useEffect, useMemo } from "react";
import TopHeader from "../components/layout/TopHeader";
import { useAuth } from "../context/AuthContext"; 
import { getGenerationReports, revertRouteGeneration } from "../services/reportsService";
import { getCityByRouteId } from "../services/routeService"; 
import { USER_ROLES } from "../constants/userRoles"; 

// Importamos los nuevos componentes separados
import ExpansionTab from "../components/reports/ExpansionTab";
import RouteActivityTab from "../components/reports/RouteActivityTab";
import GenerationTab from "../components/reports/GenerationTab";
import BusActivity from "../components/reports/BusActivityTab";

export default function ReportsPage() {
  const { token, user } = useAuth(); 
  
  const isGovernment = user?.roleId === USER_ROLES.GOVERNMENT || user?.roleId === USER_ROLES.GOVERNMENT_ADMIN;

  const [activeTab, setActiveTab] = useState(isGovernment ? "RouteActivity" : "expansion"); 
  
  // Estados de "Generation"
  const [generationData, setGenerationData] = useState([]);
  const [selectedGenRoute, setSelectedGenRoute] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeCityCards, setActiveCityCards] = useState(new Set()); 
  const [mapUpdateKey, setMapUpdateKey] = useState(0);
  const [showOldRoute, setShowOldRoute] = useState(true);
  const [showNewRoute, setShowNewRoute] = useState(true);

  // Estados del Modal de Reversión
  const [isRevertModalOpen, setIsRevertModalOpen] = useState(false);
  const [isReverting, setIsReverting] = useState(false);
  const [revertStatus, setRevertStatus] = useState(null); 

  useEffect(() => {
      setShowOldRoute(true);
      setShowNewRoute(true);
  }, [selectedGenRoute]);

  useEffect(() => {
      // Solo cargamos la data de generación si NO es gobierno y abre la pestaña
      if (activeTab === "generation" && !isGovernment) fetchGenerationReportsData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isGovernment]);

  const fetchGenerationReportsData = async () => {
      setIsLoading(true);
      try {
          const data = await getGenerationReports(token);
          if (!data || data.length === 0) {
              setGenerationData([]); setSelectedGenRoute(null); setIsLoading(false); return;
          }
          const enrichedData = [...data];
          await Promise.all(
              enrichedData.map(async (item) => {
                  try {
                      const cityInfo = await getCityByRouteId(item.rouId, token);
                      item.cityName = (cityInfo && cityInfo.citName) ? cityInfo.citName : "Ciudad Desconocida";
                  } catch (err) { item.cityName = "Error al cargar ciudad"; }
              })
          );
          setGenerationData(enrichedData);
          if (enrichedData.length > 0) { setSelectedGenRoute(enrichedData[0]); setMapUpdateKey(prev => prev + 1); }
      } catch (error) { console.error("Error al cargar reportes:", error); } finally { setIsLoading(false); }
  };

  const groupedGenerationData = useMemo(() => {
      const groups = {};
      generationData.forEach(item => {
          if (!groups[item.cityName]) groups[item.cityName] = [];
          groups[item.cityName].push(item);
      });
      return groups;
  }, [generationData]);

  useEffect(() => { setActiveCityCards(new Set(Object.keys(groupedGenerationData))); }, [groupedGenerationData]);

  const toggleCityCard = (cityName) => {
      setActiveCityCards(prev => {
          const next = new Set(prev);
          next.has(cityName) ? next.delete(cityName) : next.add(cityName);
          return next;
      });
  };

  const handleOpenRevertModal = () => { setRevertStatus(null); setIsRevertModalOpen(true); };
  const closeRevertModal = () => { setIsRevertModalOpen(false); setTimeout(() => setRevertStatus(null), 300); };

  const confirmRevert = async () => {
    if (!selectedGenRoute) return;
    setIsReverting(true); 
    setRevertStatus(null);
    try {
        await revertRouteGeneration(selectedGenRoute.rouId, token);
        const newData = generationData.filter(item => item.id !== selectedGenRoute.id);
        setGenerationData(newData);
        setSelectedGenRoute(newData.length > 0 ? newData[0] : null);
        await fetchGenerationReportsData();
        setRevertStatus('success'); 
    } catch (error) {
        console.error("Error al revertir:", error);
        setRevertStatus('error'); 
    } finally {
        setIsReverting(false);
    }
  };

  return (
    <>
      <TopHeader title="Reportes y Analíticas" />
      <div className="controls-area" style={{ position: 'relative', zIndex: 40, backgroundColor: 'white', borderBottom: '1px solid #E5E7EB' }}>
        <div className="tabs-container">
          
          {/* 🔥 OCULTAMOS LAS PESTAÑAS DE EXPANSIÓN SI ES GOBIERNO */}
          {!isGovernment && (
            <>
              <div className={`tab-item ${activeTab === 'expansion' ? 'active' : ''}`} onClick={() => setActiveTab('expansion')}>Resultados de Expansión</div>
              <div className={`tab-item ${activeTab === 'generation' ? 'active' : ''}`} onClick={() => setActiveTab('generation')}>Algoritmo de Generación</div>
            </>
          )}
          
          <div className={`tab-item ${activeTab === 'RouteActivity' ? 'active' : ''}`} onClick={() => setActiveTab('RouteActivity')}>Actividad de Rutas</div>        
          
          {/* 🔥 NUEVA PESTAÑA PARA LA ACTIVIDAD DE BUSES 🔥 */}
          <div className={`tab-item ${activeTab === 'busActivity' ? 'active' : ''}`} onClick={() => setActiveTab('busActivity')}>Actividad de Buses</div>
        
        </div>
      </div>

      {activeTab === 'expansion' && !isGovernment && <ExpansionTab />}

      {activeTab === 'generation' && !isGovernment && (
          <GenerationTab 
            isLoading={isLoading} 
            generationData={generationData} 
            groupedGenerationData={groupedGenerationData} 
            activeCityCards={activeCityCards} 
            toggleCityCard={toggleCityCard} 
            selectedGenRoute={selectedGenRoute} 
            setSelectedGenRoute={setSelectedGenRoute} 
            mapUpdateKey={mapUpdateKey} 
            showOldRoute={showOldRoute} 
            setShowOldRoute={setShowOldRoute} 
            showNewRoute={showNewRoute} 
            setShowNewRoute={setShowNewRoute} 
            handleOpenRevertModal={handleOpenRevertModal} 
          />
      )}

      {activeTab === 'RouteActivity' && <RouteActivityTab />}

      {/* 🔥 RENDERIZAMOS EL NUEVO COMPONENTE AQUÍ 🔥 */}
      {activeTab === 'busActivity' && <BusActivity />}

      {/* 🔥 MODAL INLINE CON CAMBIO DE ESTADO 🔥 */}
      {isRevertModalOpen && selectedGenRoute && !isGovernment && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(3px)', animation: 'fadeIn 0.2s ease-out' }}>
            <style>{`@keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
            <div style={{ background: 'white', borderRadius: '16px', padding: '24px', width: '90%', maxWidth: '420px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)', textAlign: revertStatus ? 'center' : 'left' }}>
                
                {!revertStatus ? (
                    // VISTA DE PREGUNTA
                    <>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '16px', color: '#DC2626' }}>
                            <div style={{ background: '#FEE2E2', padding: '8px', borderRadius: '50%' }}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>
                            </div>
                            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#1F2937' }}>Confirmar reversión</h3>
                        </div>
                        <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#4B5563', lineHeight: 1.5, textAlign: 'center' }}>
                            ¿Estás seguro de revertir los cambios realizados ayer en <strong>{selectedGenRoute.routeName}</strong>? 
                        </p>
                        <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', padding: '12px', borderRadius: '8px', marginBottom: '24px' }}>
                            <p style={{ margin: 0, fontSize: '12px', color: '#6B7280', textAlign: 'center' }}>
                                ⚠️ El algoritmo descartará la secuencia nueva (azul) y restaurará la secuencia original de paradas (rojo).
                            </p>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
                            <button onClick={closeRevertModal} disabled={isReverting} style={{ flex: 1, background: 'white', color: '#374151', border: '1px solid #D1D5DB', padding: '12px 16px', borderRadius: '8px', fontWeight: 600, cursor: isReverting ? 'not-allowed' : 'pointer', opacity: isReverting ? 0.6 : 1, transition: 'all 0.2s' }}>
                                Cancelar
                            </button>
                            <button onClick={confirmRevert} disabled={isReverting} style={{ flex: 1, background: '#EF4444', color: 'white', border: 'none', padding: '12px 16px', borderRadius: '8px', fontWeight: 600, cursor: isReverting ? 'not-allowed' : 'pointer', opacity: isReverting ? 0.7 : 1, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', transition: 'all 0.2s' }}>
                                {isReverting ? (
                                    <>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ animation: "spin 1s linear infinite" }}><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.3"></circle><path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="4" strokeLinecap="round"></path></svg>
                                        Procesando...
                                    </>
                                ) : "Sí, revertir ruta"}
                            </button>
                        </div>
                    </>
                ) : (
                    // VISTA DE ÉXITO O ERROR
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', animation: 'fadeIn 0.3s ease-out' }}>
                        <div style={{ width: '64px', height: '64px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px', background: revertStatus === 'success' ? '#D1FAE5' : '#FEE2E2', color: revertStatus === 'success' ? '#10B981' : '#EF4444' }}>
                            {revertStatus === 'success' ? (
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                            ) : (
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                            )}
                        </div>
                        <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: 800, color: '#1F2937' }}>
                            {revertStatus === 'success' ? '¡Reversión Exitosa!' : 'Error al revertir'}
                        </h3>
                        <p style={{ margin: '0 0 24px 0', fontSize: '14px', color: '#4B5563', lineHeight: 1.5, textAlign: 'center' }}>
                            {revertStatus === 'success' ? 'Los cambios han sido revertidos correctamente y la ruta original ha sido restaurada.' : 'Ocurrió un error al intentar procesar la solicitud en el servidor. Verifica tu conexión.'}
                        </p>
                        <button onClick={closeRevertModal} style={{ width: '100%', background: revertStatus === 'success' ? '#10B981' : '#EF4444', color: 'white', border: 'none', padding: '12px 16px', borderRadius: '8px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }}>
                            Entendido
                        </button>
                    </div>
                )}
            </div>
        </div>
      )}
    </>
  );
}